/**
 * @file Pure builders that turn each source's already-typed admin data
 *       into the Dashboard's {@link SourceContributionData}. This is the
 *       reference implementation of the source-contribution contract
 *       (#889) — ACCOUNT, COURSES and SECRETS are wired to their live
 *       hooks; sources without an admin data path yet (HYPERVISOR until
 *       #891, EMAIL/BILLING) get an honest "not connected" tile.
 * @module @labcraft/admin/ui/dashboard/contributions
 *
 * The builders are static + pure so they unit-test without a DOM.
 */

import type {
  NeedsYouItem,
  SourceContributionData,
  SourcesTile,
  SourceTag,
} from '../sources/contract.ts'
import type { Learner } from '../../shared/routes.ts'
import type { InstalledCourse } from '../../shared/courses_routes.ts'
import type { SecretsListResponse } from '../../shared/secrets_routes.ts'

/** A single declared-secret list entry (metadata only; value never on the wire). */
type SecretEntry = SecretsListResponse['entries'][number]

/** Age in ms past which a secret is considered stale and surfaced for rotation. */
const SECRET_STALE_MS = 90 * 86_400_000

/** The cross-source contribution builders, one static per source. */
export class DashboardSources {
  /**
   * ACCOUNT slice — the worked reference implementation of the
   * contract. Inactive learners (signed in but not yet activated by the
   * operator) are the "awaiting review" Needs-you signal.
   *
   * @param learners - The learner rows from `useLearners`.
   * @returns The ACCOUNT contribution.
   */
  static account(learners: Learner[]): SourceContributionData {
    const awaiting = learners.filter(l => l.status === 'inactive')
    const items: NeedsYouItem[] =
      awaiting.length > 0
        ? [
            {
              id: 'account-awaiting',
              source: 'ACCOUNT',
              severity: 'warning',
              title: `${awaiting.length} learner${awaiting.length === 1 ? '' : 's'} awaiting review`,
              sub: 'signed in, not yet activated',
              route: '/learners',
              posture: 'approve',
            },
          ]
        : []
    return {
      items,
      tile: {
        source: 'ACCOUNT',
        label: 'Account',
        health: 'live',
        state: awaiting.length > 0 ? 'needs-you' : 'ok',
        needsYouCount: awaiting.length,
        stat: `${learners.length} learner${learners.length === 1 ? '' : 's'} · ${awaiting.length} awaiting`,
        route: '/learners',
      },
    }
  }

  /**
   * COURSES slice — installed/enabled counts; no Needs-you signal (the
   * catalog has no incident concept).
   *
   * @param courses - Installed-course rows from `useCoursesList`.
   * @returns The COURSES contribution.
   */
  static courses(courses: InstalledCourse[]): SourceContributionData {
    const enabled = courses.filter(c => c.enabledAt !== null).length
    return {
      items: [],
      tile: {
        source: 'COURSES',
        label: 'Courses',
        health: 'live',
        state: 'ok',
        needsYouCount: 0,
        stat: `${courses.length} installed · ${enabled} enabled`,
        route: '/courses',
      },
    }
  }

  /**
   * SECRETS slice — secrets that have never been rotated or are older
   * than 90 days surface as Needs-you "rotate" items.
   *
   * @param entries - Declared-secret metadata from `useSecretsList`.
   * @param now - Current time in ms (injected for testability).
   * @returns The SECRETS contribution.
   */
  static secrets(entries: SecretEntry[], now: number): SourceContributionData {
    const stale = entries.filter(e => DashboardSources.isStaleSecret(e, now))
    const items: NeedsYouItem[] = stale.map(e => ({
      id: `secret-${e.definition.path}`,
      source: 'SECRETS',
      severity: e.version === null ? 'warning' : 'info',
      title: `Rotate ${e.definition.label}`,
      sub:
        e.version === null
          ? `${e.definition.daemon} · never rotated`
          : `${e.definition.daemon} · stale 90d+`,
      route: '/settings',
      posture: e.version === null ? 'approve' : 'remind',
    }))
    return {
      items,
      tile: {
        source: 'SECRETS',
        label: 'Secrets',
        health: 'live',
        state: stale.length > 0 ? 'needs-you' : 'ok',
        needsYouCount: stale.length,
        stat: `${entries.length} declared · ${stale.length} need rotation`,
        route: '/settings',
      },
    }
  }

  /**
   * Whether a secret needs rotation: never written, or last written
   * more than {@link SECRET_STALE_MS} ago.
   *
   * @param entry - The secret metadata.
   * @param now - Current time in ms.
   * @returns Whether the secret is stale.
   */
  static isStaleSecret(entry: SecretEntry, now: number): boolean {
    if (entry.version === null || entry.lastRotated === null) return true
    return now - new Date(entry.lastRotated).getTime() > SECRET_STALE_MS
  }

  /**
   * An honest "not connected yet" tile for a source with no admin data
   * path — a muted dot, an em-dash stat, and no Needs-you items.
   *
   * @param source - The source tag.
   * @param label - Operator-facing service name.
   * @param route - Panel path (used only once the source is live).
   * @param note - The "why it's dark" stat line.
   * @returns A not-connected contribution.
   */
  static notConnected(
    source: SourceTag,
    label: string,
    route: string,
    note: string,
  ): SourceContributionData {
    const tile: SourcesTile = {
      source,
      label,
      health: 'muted',
      state: 'not-connected',
      needsYouCount: 0,
      stat: note,
      route,
    }
    return { items: [], tile }
  }
}
